// context/AdminContext.jsx - GraphQL Integrated Version
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL Queries
const GET_JOBS_BY_GROUP = gql`
  query GetJobs($status: JobStatus, $limit: Int) {
    jobs(status: $status, limit: $limit) {
      id
      title
      description
      type
      tokenReward
      status
      groupId
      isPlatformJob
      location {
        address
        city
        state
      }
      schedule {
        date
        startTime
        endTime
        flexible
        deadline
      }
      requirements {
        minTrustScore
        requiredSkills {
          skillId
          name
          tier
        }
        preferredSkills {
          skillId
          name
          tier
        }
      }
      claimedBy
      claimedAt
      completedAt
      completionData {
        notes
        itemsProcessed
        stopsCompleted
      }
      approvalData {
        rating
        feedback
        tokensAwarded
        approvedAt
      }
      createdAt
    }
  }
`;

const CREATE_JOB = gql`
  mutation CreateJob($input: CreateJobInput!) {
    createJob(input: $input) {
      id
      title
      description
      type
      tokenReward
      status
      groupId
      isPlatformJob
      createdAt
    }
  }
`;

export const AdminContext = createContext({
  adminGroups: [],
  groupJobs: {},
  jobApplications: {},
  groupMembers: {},
  communications: {},
  analytics: {},
  tokenBudgets: {},
  loading: false,
  error: null,
  // Methods
  loadAdminData: () => {},
  postJob: () => {},
  updateJob: () => {},
  deleteJob: () => {},
  reviewApplication: () => {},
  sendMessage: () => {},
  updateGroupSettings: () => {},
  exportData: () => {},
  getGroupTokenBudget: () => {},
  getRemainingTokens: () => {},
  canPerformAdminAction: () => {},
  setGroupJobs: () => {},
  refetchJobs: () => {}
});

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const { user } = useAuth();
  
  // State for admin data
  const [adminGroups, setAdminGroups] = useState([]);
  const [groupJobs, setGroupJobs] = useState({});
  const [jobApplications, setJobApplications] = useState({});
  const [groupMembers, setGroupMembers] = useState({});
  const [communications, setCommunications] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [tokenBudgets, setTokenBudgets] = useState({});
  const [error, setError] = useState(null);

  // GraphQL Query for jobs
  const { 
    data: jobsData, 
    loading: jobsLoading, 
    refetch: refetchJobs 
  } = useQuery(GET_JOBS_BY_GROUP, {
    variables: { limit: 200 },
    skip: !user,
    fetchPolicy: 'cache-and-network'
  });

  // GraphQL Mutation for creating jobs
  const [createJobMutation] = useMutation(CREATE_JOB, {
    refetchQueries: [{ query: GET_JOBS_BY_GROUP, variables: { limit: 200 } }]
  });

  // Process jobs data into groupJobs format
  useEffect(() => {
    if (jobsData?.jobs) {
      const jobsByGroup = {};
      
      jobsData.jobs.forEach(job => {
        const groupId = job.groupId || 'platform';
        if (!jobsByGroup[groupId]) {
          jobsByGroup[groupId] = [];
        }
        
        // Transform to match expected format
        jobsByGroup[groupId].push({
          ...job,
          postedDate: job.createdAt,
          location: job.location?.city 
            ? `${job.location.city}, ${job.location.state}` 
            : 'Remote',
          minTrustScore: job.requirements?.minTrustScore || 3.0
        });
      });
      
      setGroupJobs(jobsByGroup);
    }
  }, [jobsData]);

  // Load mock data for members/analytics (can be upgraded to GraphQL later)
  useEffect(() => {
    if (user) {
      loadMockData();
    }
  }, [user]);

  const loadMockData = () => {
    // Load communications from localStorage (not critical for launch)
    const storedComms = localStorage.getItem('admin_communications') || '{}';
    setCommunications(JSON.parse(storedComms));
    
    // Generate mock analytics
    setAnalytics({});
    setGroupMembers({});
  };

  // Post a new job using GraphQL
  const postJob = useCallback(async (groupId, jobData) => {
    try {
      // Handle location - form sends string, backend expects object
      let locationInput;
      if (typeof jobData.location === 'string' && jobData.location.trim()) {
        locationInput = {
          address: jobData.location.trim(),
          city: '',
          state: ''
        };
      } else if (jobData.location && typeof jobData.location === 'object') {
        locationInput = {
          address: jobData.location.address || '',
          city: jobData.location.city || '',
          state: jobData.location.state || ''
        };
      } else {
        locationInput = {
          address: '',
          city: 'Sacramento',
          state: 'CA'
        };
      }

      const input = {
        title: jobData.title,
        description: jobData.description,
        type: jobData.type || 'delivery',
        tokenReward: jobData.tokenReward || 50,
        groupId: groupId,
        isPlatformJob: jobData.isPlatformJob || false,
        location: locationInput,
        schedule: {
          date: jobData.schedule?.date || new Date().toISOString().split('T')[0],
          startTime: jobData.schedule?.startTime || '09:00',
          endTime: jobData.schedule?.endTime || '17:00',
          flexible: jobData.schedule?.flexible ?? true,
          deadline: jobData.schedule?.deadline || jobData.deadline || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        },
        minTrustScore: jobData.minTrustScore || 3.0,
        requiredSkills: (jobData.requiredSkills || []).map(skill => ({
          skillId: skill.id || skill.skillId,
          name: skill.name || skill.skillName,
          tier: skill.tier || 1,
          category: skill.category || 'General'
        })),
        preferredSkills: (jobData.preferredSkills || []).map(skill => ({
          skillId: skill.id || skill.skillId,
          name: skill.name || skill.skillName,
          tier: skill.tier || 1,
          category: skill.category || 'General'
        }))
      };

      console.log('Creating job with input:', JSON.stringify(input, null, 2));

      const { data } = await createJobMutation({
        variables: { input }
      });
      
      return data.createJob;
    } catch (err) {
      console.error('Error posting job:', err);
      setError(err.message);
      throw err;
    }
  }, [createJobMutation]);

  // Update job (placeholder - implement GraphQL mutation later)
  const updateJob = useCallback(async (groupId, jobId, updates) => {
    console.log('updateJob not yet implemented for GraphQL', { groupId, jobId, updates });
    // TODO: Implement updateJob mutation
  }, []);

  // Delete job (placeholder - implement GraphQL mutation later)
  const deleteJob = useCallback(async (groupId, jobId) => {
    console.log('deleteJob not yet implemented for GraphQL', { groupId, jobId });
    // TODO: Implement deleteJob mutation
  }, []);

  // Review application (keep localStorage for now)
  const reviewApplication = useCallback(async (jobId, applicantId, decision, reason) => {
    try {
      const newApplication = {
        id: `app_${Date.now()}`,
        jobId,
        applicantId,
        status: decision,
        reviewedAt: new Date().toISOString(),
        reviewedBy: user?.id,
        reason
      };
      
      setJobApplications(prev => ({
        ...prev,
        [jobId]: [...(prev[jobId] || []), newApplication]
      }));
      
      return newApplication;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user]);

  // Send message (keep localStorage for now)
  const sendMessage = useCallback(async (groupId, message) => {
    try {
      const newMessage = {
        id: `msg_${Date.now()}`,
        groupId,
        from: user?.name,
        fromId: user?.id,
        subject: message.subject,
        content: message.content,
        recipients: message.recipients || 'all',
        sentAt: new Date().toISOString(),
        readBy: [],
        type: message.type || 'announcement'
      };
      
      setCommunications(prev => {
        const updated = {
          ...prev,
          [groupId]: [...(prev[groupId] || []), newMessage]
        };
        localStorage.setItem('admin_communications', JSON.stringify(updated));
        return updated;
      });
      
      return newMessage;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user]);

  // Update group settings (placeholder)
  const updateGroupSettings = useCallback(async (groupId, settings) => {
    console.log('Updating group settings:', groupId, settings);
    return settings;
  }, []);

  // Export data
  const exportData = useCallback((groupId, dataType) => {
    try {
      let data;
      switch (dataType) {
        case 'members':
          data = groupMembers[groupId] || [];
          break;
        case 'jobs':
          data = groupJobs[groupId] || [];
          break;
        case 'analytics':
          data = analytics[groupId] || {};
          break;
        default:
          data = {};
      }
      
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataType}_${groupId}_${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [groupMembers, groupJobs, analytics]);

  // Get group token budget (simplified)
  const getGroupTokenBudget = useCallback(async (groupId) => {
    return 1000; // Default budget
  }, []);

  // Get remaining tokens
  const getRemainingTokens = useCallback(async (groupId) => {
    return 1000; // Simplified
  }, []);

  // Check if user can perform admin actions
  const canPerformAdminAction = useCallback((action) => {
    // Simplified - allow all actions for now
    // In production, check user trust score
    return true;
  }, []);

  // Load admin data (wrapper for refetch)
  const loadAdminData = useCallback(() => {
    refetchJobs();
    loadMockData();
  }, [refetchJobs]);

  const value = {
    adminGroups,
    groupJobs,
    jobApplications,
    groupMembers,
    communications,
    analytics,
    tokenBudgets,
    loading: jobsLoading,
    error,
    loadAdminData,
    postJob,
    updateJob,
    deleteJob,
    reviewApplication,
    sendMessage,
    updateGroupSettings,
    exportData,
    canPerformAdminAction,
    getGroupTokenBudget,
    getRemainingTokens,
    setGroupJobs,
    refetchJobs
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
